import { useGetActiveLeadSource, useGetActiveLeadSourceDetails } from '../../hooks'
import CampaignService from '@forwardflow/services/CampaignService'
import Icon from '@forwardflow/ui/Icons'
import { Skeleton } from '@forwardflow/ui-v2'
import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { message } from 'antd'

const TYPE_OPTIONS = [
   {
      id: 'new-lead',
      label: 'New Leads',
      description: 'Only work on new incoming leads',
      color: '#4CAF50',
   },
   {
      id: 'revive',
      label: 'Revive',
      description: 'Re-engage old/cold leads',
      color: '#FF9800',
   },
   {
      id: 'activity',
      label: 'Activity',
      description: 'Trigger short campaign on lead activity',
      color: '#05DF72',
   },
]

export const CampaignTypeCard = () => {
   const { title: leadSource } = useParams()
   const [isEditing, setIsEditing] = useState(false)
   const [isSaving, setIsSaving] = useState(false)
   const [optimisticType, setOptimisticType] = useState<string | null>(null)

   const { data: activeLeadSourceData, isLoading, mutate } =
      useGetActiveLeadSource(leadSource as string)

   const { data: draftData } =
      useGetActiveLeadSourceDetails(leadSource as string)

   const hasDraftChanges =
      activeLeadSourceData?.draft?.changedSections?.includes('campaign_type') ||
      optimisticType !== null

   const liveType: string[] = Array.isArray(activeLeadSourceData?.data?.campaignType)
      ? activeLeadSourceData?.data?.campaignType
      : activeLeadSourceData?.data?.campaignType
         ? [activeLeadSourceData.data.campaignType]
         : []
   const draftType: string[] | null = hasDraftChanges
      ? Array.isArray(draftData?.data?.campaignType)
         ? draftData?.data?.campaignType
         : draftData?.data?.campaignType ? [draftData.data.campaignType] : null
      : null
   const currentTypes: string[] = (optimisticType ? JSON.parse(optimisticType) : null) ?? draftType ?? liveType

   const handleToggleType = async (typeId: string) => {
      let newTypes: string[]
      if (currentTypes.includes(typeId)) {
         newTypes = currentTypes.filter((t) => t !== typeId)
      } else {
         newTypes = [...currentTypes, typeId]
      }
      if (newTypes.length === 0) return

      setOptimisticType(JSON.stringify(newTypes))
      setIsSaving(true)

      try {
         await CampaignService.updateCampaignType(leadSource as string, newTypes)
         mutate()
      } catch (error) {
         setOptimisticType(null)
         console.error('Failed to update campaign type:', error)
         message.error('Failed to update campaign type')
      } finally {
         setIsSaving(false)
      }
   }

   if (isLoading) {
      return (
         <div className="rounded-lg bg-white/[0.05] p-5 space-y-3">
            <div className="flex items-center justify-between">
               <Skeleton height={12} width={100} radius="sm" />
               <Skeleton height={20} width={20} radius="sm" />
            </div>
            <Skeleton height={28} width={80} radius="sm" />
         </div>
      )
   }

   return (
      <div className="rounded-lg bg-white/[0.05] p-5">
         <div className="flex items-center justify-between mb-3">
            <p className="text-white/50 text-xs">Campaign Type</p>
            <button
               onClick={() => setIsEditing(!isEditing)}
               className="p-1.5 rounded-md transition-all text-white/40 hover:text-white hover:bg-white/10"
            >
               <Icon name="Edit" className="w-4 h-4" />
            </button>
         </div>

         {!isEditing ? (
            <div className="flex items-center gap-2 flex-wrap">
               {currentTypes.map((typeId) => {
                  const opt = TYPE_OPTIONS.find((o) => o.id === typeId)
                  return opt ? (
                     <div key={typeId} className="flex items-center gap-1.5 bg-white/5 rounded px-2 py-1">
                        <span
                           className="w-2 h-2 rounded-full shrink-0"
                           style={{ backgroundColor: opt.color }}
                        />
                        <span className="text-white text-xs font-semibold">{opt.label}</span>
                     </div>
                  ) : null
               })}
               {currentTypes.length === 0 && (
                  <span className="text-white/40 text-sm">Not set</span>
               )}
               {hasDraftChanges && JSON.stringify(liveType) !== JSON.stringify(currentTypes) && (
                  <span className="text-[10px] text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded">
                     draft
                  </span>
               )}
            </div>
         ) : (
            <div className="space-y-2">
               {TYPE_OPTIONS.map((option) => {
                  const isSelected = currentTypes.includes(option.id)
                  return (
                     <button
                        key={option.id}
                        disabled={isSaving}
                        onClick={() => handleToggleType(option.id)}
                        className={`w-full rounded-md p-3 text-left flex items-center gap-3 transition-all ${
                           isSelected
                              ? 'bg-[#9061DA]/20 border border-[#9061DA]/40'
                              : 'bg-white/[0.03] border border-transparent hover:bg-white/[0.06]'
                        } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                     >
                        <span
                           className="w-2.5 h-2.5 rounded-full shrink-0"
                           style={{ backgroundColor: option.color }}
                        />
                        <div>
                           <p className="text-white text-xs font-medium">
                              {option.label}
                           </p>
                           <p className="text-white/40 text-[10px]">
                              {option.description}
                           </p>
                        </div>
                     </button>
                  )
               })}
            </div>
         )}
      </div>
   )
}
